if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      constructor() {
        super();

        this.isMinimal = this.dataset.variant === 'minimal';

        this.mainButton = document.getElementById(this.dataset.mainButtonId);
        this.mainPrice = document.getElementById(this.dataset.mainPriceId);
        this.observeTarget = document.getElementById(this.dataset.observeId) || this.mainButton;

        this.submitButton = this.isMinimal
          ? this.querySelector('.sticky-add-to-cart__submit--minimal')
          : this.querySelector('.sticky-add-to-cart__submit');
        this.submitButtonText = this.submitButton?.querySelector('span');
        this.unavailableText = this.dataset.unavailableText;

        if (!this.mainButton || !this.observeTarget) return;

        this.submitButton?.addEventListener('click', this.onClick.bind(this));

        this.syncButtonState();

        this.observer = new IntersectionObserver(this.onIntersect.bind(this), { threshold: 0 });
        this.observer.observe(this.observeTarget);

        this.buttonObserver = new MutationObserver(() => {
          this.syncButtonState();
          if (!this.isMinimal) this.syncAvailability();
        });
        this.buttonObserver.observe(this.mainButton, {
          attributes: true,
          attributeFilter: ['disabled'],
          childList: true,
          subtree: true,
          characterData: true,
        });

        if (this.isMinimal) {
          this.setUpMinimal();
        } else {
          this.setUpDefault();
        }

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (event.data.sectionId !== this.dataset.sectionId) return;
            this.syncButtonState();
            if (this.isMinimal) {
              this.syncSize(event.data.variant);
              // The theme's own product-info re-render (triggered by the
              // change event below) scrolls the page back up to the main
              // form on its own — reproduces even from a direct click
              // there, so it's pre-existing theme behavior, not something
              // this bar causes. Restore now to minimize how long the
              // wrong position is visible; armScrollRestoreOnFocus (see
              // onSizeSelect) catches the actual, later cause — this is
              // just an early pass, not the full fix.
              if (this.restoreScrollY !== undefined) window.scrollTo(0, this.restoreScrollY);
            } else {
              this.syncPrice();
              this.syncMedia(event.data.variant);
              this.syncVariantSummary(event.data.variant);
              this.syncAvailability();
            }
          });
        }
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        if (this.buttonObserver) this.buttonObserver.disconnect();
        if (typeof this.variantChangeUnsubscriber === 'function') this.variantChangeUnsubscriber();
        if (this.sizeSelect) this.sizeSelect.removeEventListener('change', this.onSizeSelect);
        if (this.scheduleHeaderOffsetUpdate) {
          window.removeEventListener('resize', this.scheduleHeaderOffsetUpdate);
          window.removeEventListener('scroll', this.scheduleHeaderOffsetUpdate);
        }
        if (this.headerOffsetRafId) window.cancelAnimationFrame(this.headerOffsetRafId);
        if (this.focusRestoreHandler) document.removeEventListener('focusin', this.focusRestoreHandler);
        if (this.focusRestoreTimeout) window.clearTimeout(this.focusRestoreTimeout);
        if (this.scrollHoldRafId) window.cancelAnimationFrame(this.scrollHoldRafId);
      }

      onIntersect(entries) {
        const entry = entries[0];
        if (entry.isIntersecting) {
          this.classList.remove('sticky-add-to-cart--visible');
        } else if (entry.boundingClientRect.top < 0) {
          this.classList.add('sticky-add-to-cart--visible');
        } else {
          this.classList.remove('sticky-add-to-cart--visible');
        }
      }

      syncButtonState() {
        if (!this.submitButton) return;
        const disabled = this.mainButton.hasAttribute('disabled');
        this.submitButton.toggleAttribute('disabled', disabled);
        const mainText = this.mainButton.querySelector('span')?.textContent;
        if (this.submitButtonText && mainText) this.submitButtonText.textContent = mainText;
      }

      onClick(event) {
        event.preventDefault();
        if (this.submitButton.hasAttribute('disabled')) return;
        this.mainButton.click();
      }

      /* ============================================================
         Default variant (every template except AOUT 2026 V2) — fixed
         to the bottom of the screen, with image/price/variant summary.
         ============================================================ */

      setUpDefault() {
        this.priceWrapper = this.querySelector('.sticky-add-to-cart__price');
        this.variantSummary = this.querySelector('.sticky-add-to-cart__variant-summary');
        this.sizePrompt = this.querySelector('.sticky-add-to-cart__size-prompt');
        this.sizeSelect = this.querySelector('.sticky-add-to-cart__size-select');

        this.onSizeSelect = this.onSizeSelect.bind(this);
        this.sizeSelect?.addEventListener('change', this.onSizeSelect);

        this.syncPrice();
        this.syncAvailability();
      }

      syncPrice() {
        if (!this.mainPrice || !this.priceWrapper) return;
        this.priceWrapper.innerHTML = this.mainPrice.innerHTML;

        const priceEl = this.priceWrapper.querySelector('.price');
        if (priceEl && priceEl.classList.contains('price--on-sale')) {
          this.priceWrapper.querySelectorAll('.money').forEach((moneyEl) => {
            moneyEl.textContent = moneyEl.textContent.replace(/([.,])00(?=\D|$)/, '');
          });
        }
      }

      isUnavailable() {
        if (!this.unavailableText) return false;
        const mainText = this.mainButton.querySelector('span')?.textContent?.trim();
        return this.mainButton.hasAttribute('disabled') && mainText === this.unavailableText.trim();
      }

      syncAvailability() {
        if (!this.sizePrompt || !this.variantSummary) return;
        const unavailable = this.isUnavailable();
        this.sizePrompt.classList.toggle('hidden', !unavailable);
        this.variantSummary.classList.toggle('hidden', unavailable);
      }

      syncMedia(variant) {
        const media = this.querySelector('.sticky-add-to-cart__media img');
        if (media && variant?.featured_media?.preview_image?.src) {
          media.src = variant.featured_media.preview_image.src;
        }
      }

      syncVariantSummary(variant) {
        if (!this.variantSummary || !variant?.title) return;

        const textParts = variant.title.split(' / ');
        const valueNodes = this.variantSummary.querySelectorAll('.sticky-add-to-cart__variant-value');
        valueNodes.forEach((node, index) => {
          if (textParts[index]) node.textContent = textParts[index];
        });

        const activeSwatch = document.querySelector(
          `#variant-selects-${this.dataset.sectionId} .swatch-input__input:checked + label .swatch`
        );
        const dot = this.variantSummary.querySelector('.swatch');
        if (dot && activeSwatch) {
          dot.setAttribute('style', activeSwatch.getAttribute('style') || '');
        }
      }

      onSizeSelect(event) {
        const value = event.target.value;
        if (!value) return;

        const variantSelects = document.getElementById(`variant-selects-${this.dataset.sectionId}`);
        if (!variantSelects) return;

        if (this.isMinimal) {
          this.restoreScrollY = window.scrollY;
          this.armScrollRestoreOnFocus();
        }

        // Starts-with (not exact) match: this theme's variant-picker radios
        // render with a trailing newline in their name attribute, so an
        // exact match here silently never finds them.
        const inputName = event.target.dataset.optionName;
        const radio = variantSelects.querySelector(
          `input[name^="${CSS.escape(inputName)}"][value="${CSS.escape(value)}"]`
        );
        if (radio) {
          // Not radio.click(): a real click implicitly focuses the input,
          // and focusing an off-screen element (the main form's variant
          // picker, usually scrolled well out of view when this sticky
          // bar is visible) makes the browser auto-scroll it into view —
          // exactly the jump this must not cause. Set state + dispatch
          // the event the theme's variant-picker listens for instead.
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }

        const optionLabel = event.target.dataset.optionLabel;
        const select = variantSelects.querySelector(`select[name="options[${CSS.escape(optionLabel)}]"]`);
        if (select) {
          select.value = value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      /* ============================================================
         Minimal variant (AOUT 2026 V2 only) — pinned to the bottom of
         the screen, product type + selected size only, fixed black
         background (no adaptive theming).
         ============================================================ */

      setUpMinimal() {
        this.sizeValue = this.querySelector('.sticky-add-to-cart__value--size');
        this.sizeOptionPosition = this.dataset.sizeOptionPosition
          ? parseInt(this.dataset.sizeOptionPosition, 10)
          : null;

        // The size value is a real <select> here (not plain text) so it
        // can be changed directly from the sticky bar — same onSizeSelect
        // handler the default variant's size picker uses, which finds
        // and clicks the matching option in the real product form.
        this.sizeSelect = this.sizeValue;
        this.onSizeSelect = this.onSizeSelect.bind(this);
        this.sizeSelect?.addEventListener('change', this.onSizeSelect);

        // Desktop is pinned to the top, flush against the header; mobile
        // stays bottom-anchored (the CSS only reads this var inside the
        // min-width:750px override, so it's harmless on mobile). Measured
        // directly rather than the theme's own --header-height, which is
        // only ever set when the header's sticky behavior is enabled.
        // Re-measured on scroll (not just resize) because this theme's
        // header can shrink once scrolled past (reduce-logo-size), which
        // changes its height without firing a resize event — a one-time
        // measurement at load goes stale the moment that happens.
        this.headerEl = document.querySelector('.section-header');
        this.headerOffsetRafId = null;
        this.updateHeaderOffset = this.updateHeaderOffset.bind(this);
        this.scheduleHeaderOffsetUpdate = this.scheduleHeaderOffsetUpdate.bind(this);
        this.updateHeaderOffset();
        window.addEventListener('resize', this.scheduleHeaderOffsetUpdate);
        window.addEventListener('scroll', this.scheduleHeaderOffsetUpdate, { passive: true });
      }

      scheduleHeaderOffsetUpdate() {
        if (this.headerOffsetRafId) return;
        this.headerOffsetRafId = window.requestAnimationFrame(() => {
          this.headerOffsetRafId = null;
          this.updateHeaderOffset();
        });
      }

      updateHeaderOffset() {
        const height = this.headerEl?.offsetHeight || 0;
        this.style.setProperty('--stbar-header-height', `${height}px`);
      }

      syncSize(variant) {
        if (!this.sizeValue || !this.sizeOptionPosition || !variant?.options) return;
        const value = variant.options[this.sizeOptionPosition - 1];
        if (value) this.sizeValue.value = value;
      }

      /* The actual cause of the scroll jump: once the async product-info
         re-render this triggers finishes, the theme's own code (not
         ours — product-info.js's renderProductInfo) explicitly calls
         .focus() on the now off-screen radio in the main form, and
         focusing an off-screen element makes the browser auto-scroll it
         into view. That focus() call fires in its own promise tick,
         after PUB_SUB_EVENTS.variantChange has already published, so
         reacting to variantChange alone (see the subscriber above) is
         too early — this listens for the focus event itself instead. */
      armScrollRestoreOnFocus() {
        if (this.focusRestoreHandler) document.removeEventListener('focusin', this.focusRestoreHandler);
        if (this.focusRestoreTimeout) window.clearTimeout(this.focusRestoreTimeout);

        const targetScrollY = this.restoreScrollY;
        this.focusRestoreHandler = (event) => {
          if (this.contains(event.target)) return;
          this.clearScrollRestoreOnFocus();
          // A single correction here isn't enough — the browser's own
          // focus-into-view scroll keeps nudging position for a bit
          // afterward (the media gallery swap that's part of this same
          // update loads new images asynchronously, reflowing the page
          // above the fold each time one lands), so hold the position
          // for a short window rather than correcting only once.
          this.holdScrollPosition(targetScrollY, 600);
        };
        document.addEventListener('focusin', this.focusRestoreHandler);

        // Cleanup in case the request fails and focus never moves.
        this.focusRestoreTimeout = window.setTimeout(() => this.clearScrollRestoreOnFocus(), 5000);
      }

      holdScrollPosition(targetScrollY, durationMs) {
        if (this.scrollHoldRafId) window.cancelAnimationFrame(this.scrollHoldRafId);
        const deadline = performance.now() + durationMs;

        const tick = () => {
          if (window.scrollY !== targetScrollY) window.scrollTo(0, targetScrollY);
          if (performance.now() < deadline) {
            this.scrollHoldRafId = window.requestAnimationFrame(tick);
          } else {
            this.scrollHoldRafId = null;
          }
        };
        this.scrollHoldRafId = window.requestAnimationFrame(tick);
      }

      clearScrollRestoreOnFocus() {
        if (this.focusRestoreHandler) document.removeEventListener('focusin', this.focusRestoreHandler);
        if (this.focusRestoreTimeout) window.clearTimeout(this.focusRestoreTimeout);
        this.focusRestoreHandler = null;
        this.focusRestoreTimeout = null;
        this.restoreScrollY = undefined;
      }
    }
  );
}
